import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp, Users, CreditCard, UserCheck, Percent, Loader2, ArrowUpRight,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';

type Range = 'week' | 'month' | 'year';

type Funnel = { registered: number; startedCheckout: number; cardValidated: number; paid: number };

type Growth = {
  range: Range;
  from: string;
  to: string;
  bucket: 'day' | 'month';
  summary: {
    registrations: number;
    newSubscriptions: number;
    activeSubscribers: number;
    totalUsers: number;
    conversionRate: number;
  };
  series: { date: string; registrations: number; subscriptions: number }[];
  newByPlan: { plan: string; count: number }[];
  activeByPlan: { plan: string; count: number }[];
  funnel: Funnel;
  funnelAllTime: Funnel;
};

const RANGES: { id: Range; label: string }[] = [
  { id: 'week',  label: 'Esta semana' },
  { id: 'month', label: 'Este mes' },
  { id: 'year',  label: 'Este año' },
];

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function bucketLabel(date: string, bucket: 'day' | 'month'): string {
  if (bucket === 'month') {
    const m = Number(date.slice(5, 7)) - 1;
    return MONTHS_ES[m] ?? date;
  }
  return date.slice(8, 10); // DD
}

function fullDateLabel(date: string, bucket: 'day' | 'month'): string {
  if (bucket === 'month') {
    const m = Number(date.slice(5, 7)) - 1;
    return `${MONTHS_ES[m] ?? ''} ${date.slice(0, 4)}`;
  }
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function AdminGrowth() {
  const [range, setRange] = useState<Range>('month');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'growth', range],
    queryFn: () => api<Growth>(`/api/admin/growth?range=${range}`),
  });

  const conv = data ? Math.round(data.summary.conversionRate * 1000) / 10 : 0;

  return (
    <div className="space-y-6">
      {/* header + filters */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp size={22} className="text-brand-600" /> Crecimiento
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Registros y suscripciones de pago a lo largo del tiempo.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
          {RANGES.map((r) => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={cn(
                'px-3.5 py-1.5 rounded-md text-sm font-medium transition',
                range === r.id ? 'bg-brand-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading || !data ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-24">
          <Loader2 size={16} className="animate-spin" /> Cargando métricas…
        </div>
      ) : (
        <>
          {/* summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={<Users size={18} />} iconBg="bg-brand-50 text-brand-600"
              label="Registros" value={data.summary.registrations}
              sub={`${data.summary.totalUsers.toLocaleString('es-ES')} usuarios en total`}
            />
            <StatCard
              icon={<CreditCard size={18} />} iconBg="bg-emerald-50 text-emerald-600"
              label="Suscripciones nuevas" value={data.summary.newSubscriptions}
              sub="planes de pago iniciados"
            />
            <StatCard
              icon={<UserCheck size={18} />} iconBg="bg-violet-50 text-violet-600"
              label="Suscriptores activos" value={data.summary.activeSubscribers}
              sub="ahora mismo"
            />
            <StatCard
              icon={<Percent size={18} />} iconBg="bg-amber-50 text-amber-600"
              label="Conversión" value={`${conv}%`}
              sub="registros → suscripción"
            />
          </div>

          {/* chart */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
              <h2 className="text-sm font-semibold text-slate-700">Evolución</h2>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-brand-500" /> Registros</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Suscripciones</span>
              </div>
            </div>
            <BarChart series={data.series} bucket={data.bucket} />
          </div>

          {/* payment funnel */}
          <PaymentFunnel periodLabel={RANGES.find((r) => r.id === range)?.label ?? ''} period={data.funnel} allTime={data.funnelAllTime} />

          {/* plan breakdowns */}
          <div className="grid md:grid-cols-2 gap-4">
            <PlanBreakdown
              title="Suscripciones nuevas por plan"
              subtitle="en el periodo seleccionado"
              rows={data.newByPlan}
              accent="emerald"
            />
            <PlanBreakdown
              title="Suscriptores activos por plan"
              subtitle="ahora mismo"
              rows={data.activeByPlan}
              accent="violet"
            />
          </div>
        </>
      )}
    </div>
  );
}

/* ── Summary card ───────────────────────────────────────── */
function StatCard({ icon, iconBg, label, value, sub }: {
  icon: React.ReactNode; iconBg: string; label: string; value: number | string; sub?: string;
}) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <div className="flex items-center gap-2.5">
        <span className={cn('h-9 w-9 rounded-lg flex items-center justify-center', iconBg)}>{icon}</span>
        <span className="text-xs font-medium text-slate-500">{label}</span>
      </div>
      <p className="mt-3 text-2xl font-extrabold text-slate-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString('es-ES') : value}
      </p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

/* ── Grouped bar chart ──────────────────────────────────── */
function BarChart({ series, bucket }: { series: Growth['series']; bucket: 'day' | 'month' }) {
  if (series.length === 0) {
    return <p className="text-sm text-slate-400 py-10 text-center">Sin datos en este periodo.</p>;
  }
  const max = Math.max(1, ...series.flatMap((p) => [p.registrations, p.subscriptions]));
  // Avoid label crowding on dense daily ranges.
  const labelEvery = series.length > 16 ? Math.ceil(series.length / 12) : 1;

  return (
    <div>
      <div className="flex items-end gap-1 h-52">
        {series.map((p, i) => (
          <div key={p.date} className="flex-1 flex flex-col items-center min-w-0 h-full justify-end">
            <div className="w-full flex items-end justify-center gap-[3px] h-full">
              <div
                className="w-1/2 max-w-[12px] bg-brand-500 hover:bg-brand-600 rounded-t-sm transition-colors"
                style={{ height: `${(p.registrations / max) * 100}%` }}
                title={`${fullDateLabel(p.date, bucket)} · ${p.registrations} registros`}
              />
              <div
                className="w-1/2 max-w-[12px] bg-emerald-500 hover:bg-emerald-600 rounded-t-sm transition-colors"
                style={{ height: `${(p.subscriptions / max) * 100}%` }}
                title={`${fullDateLabel(p.date, bucket)} · ${p.subscriptions} suscripciones`}
              />
            </div>
            <span className="mt-1.5 text-[9px] text-slate-400 truncate w-full text-center h-3">
              {i % labelEvery === 0 ? bucketLabel(p.date, bucket) : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Plan breakdown ─────────────────────────────────────── */
function PlanBreakdown({ title, subtitle, rows, accent }: {
  title: string; subtitle: string; rows: { plan: string; count: number }[]; accent: 'emerald' | 'violet';
}) {
  const total = rows.reduce((a, r) => a + r.count, 0);
  const barCls = accent === 'emerald' ? 'bg-emerald-500' : 'bg-violet-500';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <h2 className="text-sm font-semibold text-slate-700">{title}</h2>
        <span className="text-[11px] text-slate-400">{subtitle}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center inline-flex items-center gap-1.5 w-full justify-center">
          Sin suscripciones <ArrowUpRight size={13} className="opacity-50" />
        </p>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <li key={r.plan}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-slate-700">{r.plan}</span>
                <span className="tabular-nums text-slate-500">{r.count.toLocaleString('es-ES')}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={cn('h-full rounded-full', barCls)} style={{ width: `${total > 0 ? (r.count / total) * 100 : 0}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Payment funnel ─────────────────────────────────────── */
const FUNNEL_STEPS: { key: keyof Funnel; label: string; hint: string }[] = [
  { key: 'registered',      label: 'Se registran',          hint: 'Crean su cuenta.' },
  { key: 'startedCheckout', label: 'Empiezan el pago',      hint: 'Pulsan un plan y llegan a la página de pago.' },
  { key: 'cardValidated',   label: 'Validan la tarjeta',    hint: 'Completan el pago de 0 € y empieza su prueba.' },
  { key: 'paid',            label: 'Pagan',                 hint: 'Al menos un cobro real tras la prueba.' },
];

function PaymentFunnel({ periodLabel, period, allTime }: { periodLabel: string; period: Funnel; allTime: Funnel }) {
  const [scope, setScope] = useState<'period' | 'all'>('period');
  const f = scope === 'period' ? period : allTime;
  const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 1000) / 10}%` : '—');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">Embudo de pago</h2>
          <p className="text-[11px] text-slate-400 mt-0.5">
            De quienes se registraron en el periodo, cuántos llegaron a cada paso (aunque fuera después).
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {([['period', periodLabel], ['all', 'Desde siempre']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setScope(id)}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition',
                scope === id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {f.registered === 0 ? (
        <p className="text-sm text-slate-400 py-6 text-center">Nadie se ha registrado en este periodo.</p>
      ) : (
        <ol className="space-y-3">
          {FUNNEL_STEPS.map((step, i) => {
            const value = f[step.key];
            const prev = i === 0 ? value : f[FUNNEL_STEPS[i - 1]!.key];
            return (
              <li key={step.key}>
                <div className="flex items-center justify-between gap-3 text-sm mb-1">
                  <span className="font-medium text-slate-700" title={step.hint}>{step.label}</span>
                  <span className="tabular-nums text-slate-500">
                    <span className="font-semibold text-slate-900">{value.toLocaleString('es-ES')}</span>
                    {i > 0 && (
                      <>
                        <span className="ml-2 text-xs">{pct(value, prev)} del paso anterior</span>
                        <span className="ml-2 text-xs text-slate-400">· {pct(value, f.registered)} del total</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-500"
                    style={{ width: `${(value / f.registered) * 100}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
